import type React from 'react'

import { useState } from 'react'
import { Button } from '@/components/ui/button'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table'
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog'
import { Loader2 } from 'lucide-react'
import { cn } from '@/lib/utils'
import { Badge } from '@/components/ui/badge'
import { formatDate } from '@/utils/format'

interface DataItem {
  id: number
  date: string
  status: 'disponivel' | 'negativo' | 'indisponivel'
  monthValue: number
}

export default function SubventionTable() {
  const [isLoading, setIsLoading] = useState(false)
  const [open, setOpen] = useState(false)

  // Mock data with status
  const allData: DataItem[] = [
    { id: 1, date: '2025-01-15', status: 'disponivel', monthValue: 1250 },
    { id: 2, date: '2025-02-15', status: 'negativo', monthValue: -300 },
    { id: 3, date: '2025-03-15', status: 'disponivel', monthValue: 1450 },
    { id: 4, date: '2025-04-15', status: 'indisponivel', monthValue: 0 },
    { id: 5, date: '2025-05-15', status: 'disponivel', monthValue: 1350 }
  ]

  // Add more mock data
  for (let i = 6; i <= 20; i++) {
    const month = i % 12 === 0 ? 12 : i % 12
    const year = 2025 + Math.floor((i - 1) / 12)

    // Randomly assign status
    const statuses: Array<'disponivel' | 'negativo' | 'indisponivel'> = ['disponivel', 'negativo', 'indisponivel']
    const randomStatus = statuses[Math.floor(Math.random() * statuses.length)]

    // Set value based on status
    let value = 1000 + Math.floor(Math.random() * 500)
    if (randomStatus === 'negativo') value = -Math.floor(Math.random() * 500)
    if (randomStatus === 'indisponivel') value = 0

    allData.push({
      id: i,
      date: `${year}-${month.toString().padStart(2, '0')}-15`,
      status: randomStatus,
      monthValue: value
    })
  }

  // Calculate total
  const totalMonthValue = allData.reduce((sum, item) => sum + item.monthValue, 0)

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (file && file.type === 'application/zip') {
      setIsLoading(true)

      // Simulate processing time
      setTimeout(() => {
        setIsLoading(false)
        setOpen(false)
      }, 3000)
    }
  }


  // Get status badge
  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'disponivel':
        return <Badge className="bg-green-500 hover:bg-green-600">Disponível</Badge>
      case 'negativo':
        return <Badge className="bg-red-500 hover:bg-red-600">Negativo</Badge>
      case 'indisponivel':
        return <Badge className="bg-gray-500 hover:bg-gray-600">Indisponível</Badge>
      default:
        return <Badge>{status}</Badge>
    }
  }

  return (
    <div className="w-full max-w-4xl mx-auto p-4">
      <div className="flex justify-end mb-4">
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button>Fazer Análise</Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Análise de Dados</DialogTitle>
            </DialogHeader>
            <div className="grid gap-4 py-4">
              {isLoading ? (
                <div className="flex flex-col items-center justify-center gap-2 py-8">
                  <Loader2 className="h-8 w-8 animate-spin text-primary" />
                  <p className="text-sm text-muted-foreground">Processando arquivo...</p>
                </div>
              ) : (
                <div className="grid gap-2">
                  <label htmlFor="zipFile" className="text-sm font-medium">
                    Envie um arquivo ZIP para análise
                  </label>
                  <input
                    id="zipFile"
                    type="file"
                    accept=".zip"
                    onChange={handleFileChange}
                    className={cn(
                      'flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ',
                      'file:border-0 file:bg-transparent file:text-sm file:font-medium bg-input'
                    )}
                  />
                </div>
              )}
            </div>
          </DialogContent>
        </Dialog>
      </div>

      <h2 className="text-lg font-semibold mb-2">Resumo de Recuperação</h2>

      <div className="border rounded-md">
        <div className="max-h-[400px] overflow-y-auto relative">
          <Table>
            <TableHeader className="sticky top-0 bg-background">
              <TableRow>
                <TableHead className="bg-background">Data</TableHead>
                <TableHead className="text-center bg-background">Situação</TableHead>
                <TableHead className="text-right bg-background">Valor do Mês</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody className="bg-background">
              {allData.map(item => (
                <TableRow key={item.id}>
                  <TableCell>{formatDate(item.date)}</TableCell>
                  <TableCell className="text-center">{getStatusBadge(item.status)}</TableCell>
                  <TableCell className="text-right">
                    {item.monthValue.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </div>

      {/* Total fixo fora da tabela */}
      <div className="mt-2 border rounded-md p-3 bg-background">
        <div className="grid grid-cols-2 font-medium ">
          <div>Total</div>
          <div className="text-right">
            {totalMonthValue.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}
          </div>
        </div>
      </div>

      <div className="mt-4 flex justify-center">
        <Button onClick={() => alert('Gerando boleto...')}>Gerar Boleto</Button>
      </div>
    </div>
  )
}
